<template>
  <span
    class="dmsuite-icon-root inline-flex shrink-0 items-center justify-center select-none"
    :style="{ width: `${computedSize}px`, height: `${computedSize}px` }"
  >
    <!-- 1. Gradient Rendered Iconify SVG via direct self-contained SVG -->
    <span
      v-if="isGradientMode && iconLoaded && fullSvgHtml"
      class="inline-flex items-center justify-center"
      v-html="fullSvgHtml"
    />

    <!-- 2. Solid Color / Standard Iconify Icon -->
    <Icon
      v-else
      :icon="iconName || fallbackIcon"
      :width="computedSize"
      :height="computedSize"
      :style="solidStyle"
      :class="['dmsuite-solid-icon', customClass]"
      aria-hidden="true"
    />
  </span>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { Icon, loadIcon, getIcon } from '@iconify/vue';
import {
  DEFAULT_ICON_COLOR,
  DEFAULT_ICON_SIZE,
  FALLBACK_ICON_NAME,
  type IconGradientConfig,
} from '@/config/dmSuiteIconPalette';
import {
  getLinearGradientCoords,
  getRadialGradientCoords,
  getGradientStopOffsets,
  normalizeGradientConfig,
} from '@/utils/iconGradient';

interface Props {
  icon?: string | null;
  size?: number | string | null;
  styleType?: 'solid' | 'gradient' | string | null;
  color?: string | null;
  gradient?: IconGradientConfig | string | null;
  customClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  icon: FALLBACK_ICON_NAME,
  size: DEFAULT_ICON_SIZE,
  styleType: 'solid',
  color: null,
  gradient: null,
  customClass: '',
});

let instanceSeq = 0;
const instanceId = Math.random().toString(36).substring(2, 9) + '-' + (++instanceSeq);
const gradientId = `dm-grad-${instanceId}`;

const iconName = computed(() => (props.icon || FALLBACK_ICON_NAME).trim());
const fallbackIcon = FALLBACK_ICON_NAME;

const computedSize = computed(() => {
  const num = Number(props.size);
  return Number.isFinite(num) && num > 0 ? num : DEFAULT_ICON_SIZE;
});

const isGradientMode = computed(() => {
  return props.styleType === 'gradient';
});

const normalizedGradient = computed<IconGradientConfig>(() => {
  let g = props.gradient;
  if (typeof g === 'string') {
    try {
      g = JSON.parse(g);
    } catch {
      g = null;
    }
  }
  return normalizeGradientConfig(g);
});

const linearCoords = computed(() => {
  return getLinearGradientCoords(normalizedGradient.value.angle ?? 135);
});

const radialCoords = computed(() => {
  return getRadialGradientCoords();
});

const stopOffsets = computed(() => {
  return getGradientStopOffsets(normalizedGradient.value.colors);
});

const solidStyle = computed(() => {
  if (!props.color || props.color === 'currentColor') {
    return {};
  }
  return {
    color: props.color,
  };
});

const rawBody = ref<string>('');
const viewWidth = ref<number>(24);
const viewHeight = ref<number>(24);
const iconLoaded = ref<boolean>(false);

const loadIconData = async () => {
  if (!isGradientMode.value) {
    return;
  }

  const name = iconName.value;
  if (!name) return;

  const cached = getIcon(name);
  if (cached) {
    rawBody.value = cached.body || '';
    viewWidth.value = cached.width || 24;
    viewHeight.value = cached.height || 24;
    iconLoaded.value = true;
    return;
  }

  try {
    const loaded = await loadIcon(name);
    if (loaded && iconName.value === name) {
      rawBody.value = loaded.body || '';
      viewWidth.value = loaded.width || 24;
      viewHeight.value = loaded.height || 24;
      iconLoaded.value = true;
    }
  } catch (err) {
    console.warn('[DMSuiteIcon] Failed to load icon data for gradient:', name, err);
    iconLoaded.value = false;
  }
};

const fullSvgHtml = computed(() => {
  if (!isGradientMode.value || !rawBody.value) return '';

  const stops = stopOffsets.value
    .map((s) => `<stop offset="${s.offset}" stop-color="${s.color}" />`)
    .join('');

  let gradientDef = '';
  if (normalizedGradient.value.type === 'radial') {
    const r = radialCoords.value;
    gradientDef = `<radialGradient id="${gradientId}" cx="${r.cx}" cy="${r.cy}" r="${r.r}" fx="${r.fx}" fy="${r.fy}">${stops}</radialGradient>`;
  } else {
    const l = linearCoords.value;
    gradientDef = `<linearGradient id="${gradientId}" x1="${l.x1}" y1="${l.y1}" x2="${l.x2}" y2="${l.y2}">${stops}</linearGradient>`;
  }

  const replaced = rawBody.value.replace(/currentColor/g, `url(#${gradientId})`);
  return `<svg viewBox="0 0 ${viewWidth.value} ${viewHeight.value}" width="${computedSize.value}" height="${computedSize.value}" class="dmsuite-gradient-svg ${props.customClass}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><defs>${gradientDef}</defs><g fill="url(#${gradientId})" stroke="url(#${gradientId})">${replaced}</g></svg>`;
});

watch(
  [iconName, isGradientMode],
  () => {
    loadIconData();
  },
  { immediate: true }
);

onMounted(() => {
  loadIconData();
});
</script>

<style scoped>
.dmsuite-icon-root {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  vertical-align: middle;
}
.dmsuite-gradient-svg {
  display: block;
}
</style>
